import { useMemo } from "react"
import type { View } from "react-big-calendar"
import useDate from "~/ui-rtk/hooks/date"

export type AgendaRange = {
  /** ISO start of the visible window (inclusive), for the list query. */
  from: string
  /** ISO end of the visible window (inclusive), for the list query. */
  to: string
}

/**
 * Query window matching what react-big-calendar renders for a view + date:
 * month view shows full weeks around the month, agenda view shows the next
 * 30 days (the library default agenda length).
 */
export function useAgendaRange(view: View, date: Date): AgendaRange {
  const dayjs = useDate()

  return useMemo(() => {
    const anchor = dayjs(date)

    switch (view) {
      case "day":
        return {
          from: anchor.startOf("day").toISOString(),
          to: anchor.endOf("day").toISOString(),
        }
      case "week":
      case "work_week":
        return {
          from: anchor.startOf("week").toISOString(),
          to: anchor.endOf("week").toISOString(),
        }
      case "agenda":
        return {
          from: anchor.startOf("day").toISOString(),
          to: anchor.add(30, "day").endOf("day").toISOString(),
        }
      case "month":
      default:
        return {
          from: anchor.startOf("month").startOf("week").toISOString(),
          to: anchor.endOf("month").endOf("week").toISOString(),
        }
    }
  }, [dayjs, view, date])
}
